import { makeObservable, observable, runInAction, computed } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';
import BaseAdminStore, { AdminApiResponse } from '../../../shared/stores/baseAdminStore';
import { MatenRequestResponse } from '../../../shared';

interface AIProvider {
    id: string;
    apiKey: boolean;
    settings?: any;
}

interface AIProviderItem {
    name: string;
    id: string;
}
export class KihubDatabaseStore {
    public id: number;
    public vectorStoreId: string;
    public label: string;
    public description: string;
    public enabled: boolean;

    constructor(store: any) {
        makeObservable(this, {
            id: observable,
            vectorStoreId: observable,
            label: observable,
            description: observable,
            enabled: observable,
        });

        this.id = store.id;
        this.vectorStoreId = store.vectorStoreId;
        this.label = store.label;
        this.description = store.description;
        this.enabled = store.enabled;
    }

    public async edit(sdk: ApplicationSdk, store: KihubAdminStore): Promise<void> {
        const databaseEditResponse = await sdk.getDialogMultiInput({
            title: 'Wissensdatenbank bearbeiten',
            fields: [
                {
                    label: 'Wissensdatenbank Name',
                    key: 'label',
                    type: 'text',
                },
                {
                    label: 'Wissensdatenbank Beschreibung',
                    key: 'description',
                    type: 'text',
                },
            ],
            initialValue: {
                label: this.label,
                description: this.description,
            },
            message: 'Hier können Sie die Einstellungen der Wissensdatenbank bearbeiten.',
            onConfirm: async (values: any) =>
                store.saveStore(this.id, values.label, values.description),
        });

        if (databaseEditResponse.confirmed) {
            runInAction(() => {
                const { label, description } =
                    (
                        databaseEditResponse.response as {
                            data?: { label?: string; description?: string };
                        }
                    )?.data || {};
                this.label = label || '';
                this.description = description || '';
            });

            sdk.showSuccessToast('Wissensdatenbank erfolgreich aktualisiert.');
        } else if (databaseEditResponse.error) {
            switch (databaseEditResponse.error) {
                default:
                    sdk.showErrorToast(
                        `Unbekannter Fehler beim Aktualisieren der Wissensdatenbank`
                    );
                    break;
            }
        }
    }

    public async activate(store: KihubAdminStore): Promise<MatenRequestResponse> {
        const enableResponse: MatenRequestResponse = await store.enableStore(this.id);
        runInAction(() => {
            if (enableResponse.success) {
                this.enabled = true; // Update enabled status after successful activation
            }
        });
        return enableResponse;
    }
}

/**
 * The KIHub Admin Store for managing admin settings
 */
class KihubAdminStore extends BaseAdminStore {
    public provider: AIProvider | null = null;
    public providerList: AIProviderItem[] = [];
    public providerOptions: any[] = [];
    public kihubDatabases: KihubDatabaseStore[] = [];

    public settings: any = {};

    constructor(sdk: ApplicationSdk) {
        super(sdk);

        makeObservable(this, {
            provider: observable,
            providerList: observable,
            providerOptions: observable,
            kihubDatabases: observable,
            settings: observable,
            hasApiKey: computed,
            hasProvider: computed,
        });
    }

    // Implement required abstract methods
    protected getApiEndpoint(): string {
        return 'kihub';
    }

    protected getApiPath(): string {
        return 'admin/get';
    }

    protected buildApiParams(): any {
        return {};
    }

    protected processApiResponse(response: AdminApiResponse): any {
        runInAction(() => {
            this.provider = response.json.provider || null;
            this.providerList = response.json.providerList || [];
            this.providerOptions = response.json.providerOptions || [];
            this.kihubDatabases = (response.json.kihubStores || []).map(
                (store: any) => new KihubDatabaseStore(store)
            );
            this.settings = this.provider?.settings || {};
        });

        return response.json;
    }

    public async saveApiKey(apiKey: string): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('kihub', 'admin/api-key/update', {
                apiKey,
            });

            if (response.status === 200) {
                this.fetchData();
                return { success: true, status: 200 };
            } else if (response.status === 400) {
                return {
                    success: false,
                    status: 400,
                    error: response.json.error || 'UNKNOWN_ERROR',
                };
            }
        } catch (error) {
            return { success: false, status: 400, error: 'Unbekannter Fehler' };
        }

        return { success: false };
    }

    public async enableStore(storeId: number): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('kihub', 'admin/module/update', {
                storeId,
                enable: true,
            });

            if (response.status === 200) {
                return { success: true, status: 200, data: response.json };
            } else {
                return { success: false, status: response.status, error: response.json.error };
            }
        } catch (error) {
            return { success: false, status: 500 };
        }
    }

    public async saveStore(
        storeId: number,
        label: string,
        description: string
    ): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('kihub', 'admin/module/update', {
                storeId,
                label: label,
                description: description,
            });

            if (response.status === 200) {
                return { success: true, status: 200, data: response.json };
            } else {
                return { success: false, status: response.status, error: response.json.error };
            }
        } catch (error) {
            return { success: false, status: 500 };
        }
    }

    public async changeProvider(providerId: string): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('kihub', 'admin/provider/update', {
                providerId,
            });

            if (response.status === 200) {
                runInAction(() => {
                    this.provider = {
                        id: providerId,
                        apiKey: false,
                    };
                });
                return { success: true, status: 200, data: true };
            } else {
                return { success: false, status: response.status, error: response.json.error };
            }
        } catch (error) {
            return { success: false, status: 500 };
        }
    }

    public async saveWebhookSettings(
        moduleLearnUrl: string,
        moduleDeleteUrl: string,
        moduleEntryDeleteUrl: string
    ): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('kihub', 'admin/provider/custom/update', {
                webhookModuleLearn: moduleLearnUrl,
                webhookModuleDelete: moduleDeleteUrl,
                webhookModuleEntryDelete: moduleEntryDeleteUrl,
            });

            if (response.status === 200) {
                return { success: true, status: 200, data: response.json };
            } else {
                return { success: false, status: response.status, error: response.json.error };
            }
        } catch (error) {
            return { success: false, status: 500, error: 'Unbekannter Fehler' };
        }
    }

    public async init(): Promise<void> {
        await super.init();
    }

    get hasProvider(): boolean {
        return this.provider !== null;
    }

    get hasApiKey(): boolean {
        return this.provider?.apiKey === true;
    }

    get isKiHub() {
        return true;
    }
}

export default KihubAdminStore;
